import type { ApiMediaVideo } from '@/types/api'
import type { MediaPlatform, MediaVideo } from '@/types/media'
import { isGenericMediaFallback } from '@/lib/instagramThumbnail'
import { flushSync } from 'react-dom'

const FALLBACK_THUMB =
  'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800&h=500&fit=crop&q=80'

export function normalizeMediaVideo(raw: ApiMediaVideo): MediaVideo {
  return {
    id: raw.id,
    slug: raw.slug,
    title: raw.title,
    shortDescription: raw.shortDescription,
    platform: (raw.platform as MediaPlatform) || 'instagram',
    socialUrl: raw.socialUrl,
    embedUrl: raw.embedUrl,
    thumbnail: raw.thumbnail && !isGenericMediaFallback(raw.thumbnail) ? raw.thumbnail : null,
    isFeatured: raw.isFeatured,
    showOnHomepage: raw.showOnHomepage,
    displayOrder: raw.displayOrder,
    publishedAt: raw.publishedAt,
    expiresAt: raw.expiresAt,
    category: raw.category
      ? {
          id: raw.category.id,
          name: raw.category.name,
          slug: raw.category.slug,
          displayOrder: raw.category.displayOrder,
        }
      : null,
  }
}

export function extractYoutubeId(link: string): string | null {
  const raw = link.trim()
  if (!raw) return null
  if (!raw.includes('/') && !raw.includes('.')) return raw

  try {
    if (raw.includes('youtube.com/watch')) {
      return new URL(raw).searchParams.get('v')
    }
    if (raw.includes('youtu.be/')) {
      return raw.split('youtu.be/')[1]?.split('?')[0] ?? null
    }
    if (raw.includes('youtube.com/embed/')) {
      return raw.split('embed/')[1]?.split('?')[0] ?? null
    }
    if (raw.includes('youtube.com/shorts/')) {
      return raw.split('shorts/')[1]?.split('?')[0] ?? null
    }
  } catch {
    // fall through
  }

  const match = raw.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/,
  )
  return match?.[1] ?? null
}

export function getMediaEmbedUrl(video: MediaVideo, autoplay = false): string | null {
  if (video.platform === 'youtube') {
    const id =
      extractYoutubeId(video.socialUrl) ||
      extractYoutubeId(video.embedUrl ?? '')
    if (!id) return video.embedUrl ?? null

    const params = new URLSearchParams({
      autoplay: autoplay ? '1' : '0',
      mute: autoplay ? '1' : '0',
      rel: '0',
      modestbranding: '1',
      playsinline: '1',
    })
    return `https://www.youtube.com/embed/${id}?${params.toString()}`
  }

  if (video.platform === 'instagram') {
    const match = video.socialUrl.match(/instagram\.com\/(reel|p|tv)\/([A-Za-z0-9_-]+)/i)
    if (!match) return video.embedUrl ?? null

    const base = `https://www.instagram.com/${match[1]}/${match[2]}/embed`
    if (!autoplay) return base

    const params = new URLSearchParams({
      autoplay: '1',
      play: '1',
    })
    return `${base}?${params.toString()}`
  }

  if (video.platform === 'vimeo') {
    const match = video.socialUrl.match(/vimeo\.com\/(?:video\/)?(\d+)/i)
    const id = match?.[1] ?? video.embedUrl?.match(/video\/(\d+)/)?.[1]
    if (!id) return video.embedUrl ?? null

    const params = new URLSearchParams({
      autoplay: autoplay ? '1' : '0',
      muted: autoplay ? '1' : '0',
      title: '0',
      byline: '0',
      portrait: '0',
    })
    return `https://player.vimeo.com/video/${id}?${params.toString()}`
  }

  if (video.platform === 'facebook') {
    const params = new URLSearchParams({
      href: video.socialUrl,
      show_text: 'false',
      width: '560',
    })
    if (autoplay) params.set('autoplay', 'true')
    return `https://www.facebook.com/plugins/video.php?${params.toString()}`
  }

  return video.embedUrl ?? null
}

export function formatMediaDate(date?: string | null) {
  if (!date) return undefined
  return new Date(date).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

export function isInstagramReel(video: MediaVideo) {
  return video.platform === 'instagram' && /instagram\.com\/reel\//i.test(video.socialUrl)
}

/** Mount reels viewer synchronously so autoplay can use the opening click gesture. */
export function openReelsViewer(
  video: MediaVideo,
  reelVideos: MediaVideo[],
  setStartIndex: (index: number) => void,
  setOpen: (open: boolean) => void,
) {
  const index = reelVideos.findIndex((v) => v.id === video.id)
  flushSync(() => {
    setStartIndex(index >= 0 ? index : 0)
    setOpen(true)
  })
}

export const MEDIA_FALLBACK_THUMB = FALLBACK_THUMB
